// =============================================================================
// FILE: src/lib/supabase.js
// MODULE: Core — Supabase Client
// PROJECT: Arcus Sports Management
//
// DESCRIPTION:
//   Creates and exports the single Supabase client instance used by the
//   entire frontend application. Importing this file from multiple places
//   always returns the same instance — do not call createClient() elsewhere.
//
// USAGE:
//   import { supabase } from '@/lib/supabase'
//   const { data, error } = await supabase.from('tournaments').select('*')
//
// SECURITY:
//   - Uses the ANON key — safe to expose in the browser bundle
//   - Supabase RLS policies enforce all data access rules
//   - Never use the SERVICE ROLE key on the frontend
//
// AUTH STORAGE:
//   The session is persisted in localStorage by default (Supabase default).
//   This is acceptable for web SPAs. The JWT is short-lived (1 hour) and
//   auto-refreshed by the Supabase client before expiry.
//
// DEPENDENCIES:
//   @supabase/supabase-js — installed via npm
//   VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY — set in .env.local
// =============================================================================

import { createClient } from '@supabase/supabase-js'

// ---------------------------------------------------------------------------
// Validate environment variables at startup.
// Fail loudly in development if config is missing — better than a silent
// "invalid URL" error buried in a network request.
// ---------------------------------------------------------------------------
const supabaseUrl  = import.meta.env.VITE_SUPABASE_URL
const supabaseAnon = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnon) {
  throw new Error(
    '[Arcus] Missing Supabase environment variables.\n' +
    'Copy .env.example to .env.local and fill in your values.\n' +
    'Required: VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY'
  )
}

// ---------------------------------------------------------------------------
// Create the singleton client.
// Options explained:
//   auth.autoRefreshToken  — automatically refresh the JWT before it expires
//   auth.persistSession    — store session in localStorage across page reloads
//   auth.detectSessionInUrl — handle magic link / OAuth redirect callbacks
//   global.headers         — add app identifier to all requests for debugging
// ---------------------------------------------------------------------------
export const supabase = createClient(supabaseUrl, supabaseAnon, {
  auth: {
    autoRefreshToken:   true,
    persistSession:     true,
    detectSessionInUrl: true,   // Required for email magic links and OAuth
  },
  global: {
    headers: {
      'x-app-name': 'arcus-sports-management',
    },
  },
})

// ---------------------------------------------------------------------------
// Named exports for common Supabase sub-clients.
// These are convenience re-exports — same underlying client instance.
// ---------------------------------------------------------------------------

/** Supabase Auth sub-client — use for login, signup, session management */
export const auth = supabase.auth

/** Supabase Storage sub-client — use for logo and avatar uploads */
export const storage = supabase.storage
